/**
 * MusicMixButton — per-story "Music mix" tool. Generates a soft AI
 * instrumental (fal.ai stable-audio via the Universal Key), loops it
 * under the narration with auto-ducking, and offers a clean MP4
 * download ready for social posting.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Music, Loader2, Download, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = [
  { id: "soft_acoustic", label: "Soft Acoustic Guitar" },
  { id: "gentle_piano", label: "Gentle Piano" },
  { id: "warm_strings", label: "Warm Strings" },
  { id: "ambient_calm", label: "Calm Ambient" },
];

const VOLUMES = [
  { id: 0.22, label: "Subtle" },
  { id: 0.4, label: "Medium" },
  { id: 0.6, label: "Strong" },
];

export default function MusicMixButton({ video }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState("soft_acoustic");
  const [volume, setVolume] = useState(0.4);
  const [mix, setMix] = useState(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/videos/${video.id}/music-mix`);
      setMix(data);
      if (data.status !== "working" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (data.status === "done") toast.success("Music version ready to download");
        if (data.status === "failed") toast.error(data.message || "Music mix failed");
      }
    } catch (_e) { /* ignored */ }
  };

  useEffect(() => {
    if (open) refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const start = async () => {
    try {
      await axios.post(`${API}/videos/${video.id}/music-mix`, { style, volume });
      setMix({ status: "working", message: "Starting…" });
      if (!pollRef.current) pollRef.current = setInterval(refresh, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the music mix");
    }
  };

  const working = mix?.status === "working";
  const done = mix?.status === "done" && mix?.url;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-[0.14em] px-2 py-1.5 rounded-md border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
        data-testid={`music-mix-open-${video.id}`}
        title="Create a downloadable version of this video with soft AI background music"
      >
        <Music className="w-3.5 h-3.5" /> Music mix
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-md rounded-xl border border-white/10 bg-[#14120d] p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="music-mix-modal"
          >
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg">Music version</h3>
              <button onClick={() => setOpen(false)} className="text-[color:var(--muted)] hover:text-white" data-testid="music-mix-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[color:var(--muted)] mb-4">
              Composes a soft instrumental with AI, loops it gently under the
              narration (auto-ducked so the voice stays clear) and gives you a
              clean MP4 to post anywhere. Uses a small amount of Universal Key
              credits per track — the track is saved, so re-mixing is free.
            </p>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  className={`text-xs px-3 py-2 rounded-lg border transition-colors ${style === s.id ? "border-[color:var(--accent)] text-[color:var(--accent)] bg-[color:var(--accent)]/10" : "border-white/10 text-[color:var(--muted)] hover:border-white/30"}`}
                  data-testid={`music-style-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--muted)] mb-2">Music volume</div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {VOLUMES.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVolume(v.id)}
                  className={`text-xs px-3 py-2 rounded-lg border transition-colors ${volume === v.id ? "border-[color:var(--accent)] text-[color:var(--accent)] bg-[color:var(--accent)]/10" : "border-white/10 text-[color:var(--muted)] hover:border-white/30"}`}
                  data-testid={`music-volume-${v.label.toLowerCase()}`}
                >
                  {v.label}
                </button>
              ))}
            </div>

            {working && (
              <div className="flex items-center gap-2 text-sm text-[color:var(--accent)] mb-3" data-testid="music-mix-progress">
                <Loader2 className="w-4 h-4 animate-spin" />
                {mix?.message || "Working…"}
              </div>
            )}
            {mix?.status === "failed" && (
              <div className="text-xs text-red-300 mb-3" data-testid="music-mix-error">{mix.message}</div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={start}
                disabled={working}
                className="btn-primary text-sm px-4 py-2 flex-1 disabled:opacity-40"
                data-testid="music-mix-start"
              >
                {working ? "Composing…" : done ? "Re-make with this style" : "Create music version"}
              </button>
              {done && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${mix.url}`}
                  download
                  className="text-sm px-4 py-2 rounded-lg border border-[color:var(--accent)] text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 flex items-center gap-1.5"
                  data-testid="music-mix-download"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
